import type { OneOfExpression } from '@rtkwlf/asset-data-service';
import type { Dayjs } from 'dayjs';

import {
  and,
  any,
  gte,
  lte,
  range,
  like,
  isnull,
} from '@apis/asset-data-service/filter-operations/filter-operations';
import type { DateRange } from '@packages/fenrir/components/date-pickers';

import type { Filters } from '../../../../assets/device-list/asset-filters/filters';
import { isFilterEmpty } from '../../../../assets/device-list/asset-filters/is-filter-empty/is-filter-empty';

const dateRange = (name: string, [from, to]: DateRange<Dayjs>) => {
  if (from && to) return range(name, from.toISOString(), to.toISOString());

  if (from) return gte(name, from.toISOString());

  if (to) return lte(name, to.toISOString());

  return undefined;
};

const getFilterQuery = (key: keyof Filters, filters: Filters) => {
  if (key === 'deletedTime') return isnull('deleted_time');

  if (!filters[key] || isFilterEmpty(filters[key])) return undefined;

  switch (key) {
    case 'categories':
      return any('category', filters[key]);
    case 'importances':
      return any('importance', filters[key]);
    case 'sources':
      return any('sources', filters[key]);
    case 'tags':
      return any('tags', filters[key]);
    case 'lastScan':
      return dateRange('risk.last_successful_scanned', filters[key]);
    case 'lastSeen':
      return dateRange('last_seen_time', filters[key]);
    case 'riskScore': {
      const [from, to] = filters[key];

      if (from === null || to === null) return;

      return range('risk.score', from.toString(), to.toString());
    }
    case 'name':
      return like('name', filters[key]);
    case 'hostname':
      return like('hostname', filters[key]);
    case 'os':
      return like('os.name', filters[key]);
    case 'mac':
      return like('network_interfaces.mac', filters[key]);
    case 'ipAddress':
      return like('network_interfaces.ip_addresses', filters[key]);
    default:
      return key satisfies never;
  }
};

export const getFilterParam = (
  filters: Filters,
): OneOfExpression | undefined => {
  const queries = Object.keys(filters).reduce((queries, key) => {
    const query = getFilterQuery(key as keyof Filters, filters);

    if (query) queries.push(query);

    return queries;
  }, [] as OneOfExpression[]);

  if (!queries.length) return undefined;

  if (queries.length === 1) return queries[0];

  return and(...queries);
};
